import React, { useEffect, useState, useMemo } from "react";
import ProductCard from "./ProductCard";
import { Divider, FormControl, InputLabel, MenuItem, Pagination, Select, useTheme, useMediaQuery } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchAllProducts, searchProduct } from "../../../State/customer/productSlice";

const Product = () => {
    const [sort, setSort] = useState<string>("");
    const [page, setPage] = useState<number>(1);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const dispatch = useAppDispatch();
    const [searchParam] = useSearchParams();
    const { category } = useParams();

    const { products, searchProduct: searchResults, loading } = useAppSelector((state) => state.product);

    const handleSortChange = (event: any) => {
        setSort(event.target.value);
    };

    const handlePageChange = (value: number) => {
        setPage(value);
    };

    const searchQuery = searchParam.get("search") || "";

    const displayProducts = searchQuery ? searchResults : products;

    useEffect(() => {
        if (searchQuery) {
            dispatch(searchProduct(searchQuery));
            return;
        }

        const [minPrice, maxPrice] = searchParam.get("price")?.split("-") || [];
        const color = searchParam.get("color") || "";
        const stock = searchParam.get("stock") || "";
        const discount = searchParam.get("discount") || "";

        const rawParams: Record<string, any> = {
            category,
            minPrice: minPrice ? Number(minPrice) : undefined,
            maxPrice: maxPrice ? Number(maxPrice) : undefined,
            color,
            stock,
            discount: discount ? Number(discount) : undefined,
            sort,
            pageNumber: page - 1,
        };

        const cleanParams = Object.keys(rawParams).reduce((acc: Record<string, any>, key) => {
            const value = rawParams[key];
            if (value !== "" && value !== undefined && value !== null) {
                acc[key] = value;
            }
            return acc;
        }, {});

        dispatch(fetchAllProducts(cleanParams));
    }, [category, searchQuery, searchParam, sort, page, dispatch]);

    const sortedProducts = useMemo(() => {
        if (!displayProducts) return [];
        const productsCopy = [...displayProducts];

        if (sort === "price_low") {
            return productsCopy.sort((a, b) =>
                (a.sellingPrice || a.mrpPrice) - (b.sellingPrice || b.mrpPrice)
            );
        }
        if (sort === "price_high") {
            return productsCopy.sort((a, b) =>
                (b.sellingPrice || b.mrpPrice) - (a.sellingPrice || a.mrpPrice)
            );
        }
        return productsCopy;
    }, [displayProducts, sort]);

    const pageHeaderTitle = () => {
        if (searchQuery) return `Search: "${searchQuery}"`;
        if (category) return category.replace("-", " ");
        return "Products";
    };

    return (
        <div className="w-full mt-2 min-h-screen">
            {/* Header Controls Bar */}
            <div className="flex flex-row items-center justify-between px-3 sm:px-8 py-3 gap-2 sm:gap-4 w-full">
                <h1 className="text-base sm:text-2xl font-bold text-gray-800 uppercase tracking-wide truncate flex-1 pr-2">
                    {pageHeaderTitle()}
                </h1>

                <FormControl size="small" className="w-[120px] sm:w-[180px] shrink-0">
                    <InputLabel id="sort-select-label">Sort</InputLabel>
                    <Select
                        labelId="sort-select-label"
                        id="sort-simple-select"
                        value={sort}
                        label="Sort"
                        onChange={handleSortChange}
                    >
                        <MenuItem value="">Default</MenuItem>
                        <MenuItem value={"price_low"}>Price: Low - High</MenuItem>
                        <MenuItem value={"price_high"}>Price: High - Low</MenuItem>
                    </Select>
                </FormControl>
            </div>

            <Divider />

            {/* Products & Main Container */}
            <div className="pt-4 pb-10">
                <div className="w-full space-y-6">
                    {/* Products Responsive Grid */}
                    <section className="products_section grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-5 px-3 sm:px-6 justify-center">
                        {loading ? (
                            <div className="flex justify-center items-center py-20 col-span-full">
                                <p className="text-gray-500 font-medium animate-pulse">Loading products...</p>
                            </div>
                        ) : sortedProducts.length === 0 ? (
                            <div className="flex flex-col justify-center items-center py-20 col-span-full">
                                <p className="text-gray-600 font-semibold text-lg">No products found.</p>
                                <p className="text-gray-400 text-sm mt-1">Try tweaking your search or filter settings.</p>
                            </div>
                        ) : (
                            sortedProducts.map((item) => <ProductCard key={item.id} item={item} />)
                        )}
                    </section>

                    {/* Pagination */}
                    {!searchQuery && sortedProducts.length > 0 && (
                        <div className="flex justify-center pt-6 pb-4 px-2">
                            <Pagination
                                page={page}
                                onChange={(e, value) => handlePageChange(value)}
                                count={10}
                                variant="outlined"
                                color="primary"
                                size={isMobile ? "small" : "medium"}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Product;